import {
    Badge,
    Button,
    Card,
    CardBody,
    Divider,
    Flex,
    FormControl,
    FormErrorMessage,
    FormHelperText,
    FormLabel,
    Heading,
    Image,
    Input,
    Spinner,
    Text,
    useToast,
    VStack
} from '@chakra-ui/react';
import { ChangeEvent, FormEvent, useState } from 'react';
import { checkDocument, RegistrationFormData, submitRegistration } from '../services/api';
import { validateForm } from '../utils/validations';

const logoPath = "/assets/logos/LOGO_ITALIA.png";
const OPENING_DATE = new Date('2026-05-25T00:00:00-03:00');

const RegistrationForm = () => {
  const [formData, setFormData] = useState<RegistrationFormData>({
    name: '',
    email: '',
    document: ''
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [registeredEmail, setRegisteredEmail] = useState<string>('');
  
  const toast = useToast();

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Remove o erro do campo quando o usuário digita
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    // Valida o formulário
    const validation = validateForm(formData.name, formData.email, formData.document);
    
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Verifica se o documento já está cadastrado
      const checkResult = await checkDocument(formData.document);
      
      if (checkResult.exists) {
        setErrors({
          document: 'Este documento já está cadastrado'
        });
        
        toast({
          title: 'Documento já cadastrado',
          description: 'O documento informado já está cadastrado no sistema.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        
        setIsSubmitting(false);
        return;
      }
      
      // Envia o formulário
      const result = await submitRegistration(formData);
      
      if (result.success) {
        setRegisteredEmail('2giugnoconfirma@sabrieventos.tec.br');
        setIsSuccess(true);
        
        // Limpa o formulário
        setFormData({
          name: '',
          email: '',
          document: ''
        });
        
        setErrors({});
      } else {
        throw new Error(result.message || 'Erro ao processar confirmação');
      }
    } catch (error) {
      console.error('Erro ao enviar formulário:', error);
      
      toast({
        title: 'Erro ao enviar formulário',
        description: 'Ocorreu um erro ao processar sua confirmação. Por favor, tente novamente.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const registrationsOpen = new Date() >= OPENING_DATE;

  if (!registrationsOpen) {
    const diasRestantes = Math.ceil((OPENING_DATE.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
    return (
      <Card boxShadow="lg" p={4} borderRadius="md" bg="white" width={{ base: '95%', md: '520px' }}>
        <CardBody>
          <VStack spacing={5} align="center">
            <Image src={logoPath} alt="Logo Consulado" maxH="80px" />
            <Heading size="md" textAlign="center" color="gray.700">
              Confirmação para Festa Nacional da Itália
            </Heading>
            <Text fontSize="lg" fontWeight="bold" color="gray.700" textAlign="center">
              02 de junho de 2026
            </Text>
            <Divider />
            <Badge colorScheme="yellow" fontSize="sm" px={3} py={1} borderRadius="full">
              Em breve
            </Badge>
            <Heading size="lg" textAlign="center" color="italy.red" style={{ color: '#cf2b36' }}>
              As inscrições abrem em
            </Heading>
            <Flex
              bg="gray.50"
              border="2px solid"
              borderColor="gray.200"
              borderRadius="xl"
              px={8}
              py={4}
              direction="column"
              align="center"
            >
              <Text fontSize="5xl" fontWeight="extrabold" color="gray.800" lineHeight="1">
                {diasRestantes}
              </Text>
              <Text fontSize="md" color="gray.500" fontWeight="medium">
                {diasRestantes === 1 ? 'dia' : 'dias'}
              </Text>
            </Flex>
            <Text fontSize="md" color="gray.600" textAlign="center">
              As inscrições para o evento estarão disponíveis a partir do dia{' '}
              <Text as="strong" color="gray.800">25 de maio de 2026</Text>.
            </Text>
            <Text fontSize="sm" color="gray.500" textAlign="center">
              Volte nessa data para confirmar sua presença.
            </Text>
          </VStack>
        </CardBody>
      </Card>
    );
  }

  if (isSuccess) {
    return (
      <Card boxShadow="md" p={4} borderRadius="md" bg="white" width={{ base: '95%', md: '500px' }}>
        <CardBody>
          <VStack spacing={6} align="center">
            <Image src={logoPath} alt="Logo Consulado" maxH="80px" />
            <Heading size="md" textAlign="center" color="green.600">
              Confirmação Recebida!
            </Heading>
            <Text textAlign="center" fontSize="md">
              Obrigada pelo envio das informações, aguarde que a nossa produção enviará o seu QR Code de acesso pelo e-mail: <strong>{registeredEmail}</strong>
            </Text>
          </VStack>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card boxShadow="md" p={4} borderRadius="md" bg="white" width={{ base: '95%', md: '500px' }}>
      <CardBody>
        <Flex direction="column" align="center" mb={6}>
          <Image src={logoPath} alt="Logo Consulado" maxH="80px" mb={4} />
          <Heading size="md" textAlign="center">
            Confirmação para Festa Nacional da Itália <br /> 02 de junho de 2026
          </Heading>
          <Text fontSize="sm" textAlign="center" mt={2} color="gray.600">
            Por favor, preencha o formulário abaixo para confirmar sua presença no evento.
          </Text>
        </Flex>
        
        <form onSubmit={handleSubmit}>
          <VStack spacing={4}>
            <FormControl isInvalid={!!errors.name} isRequired>
              <FormLabel>Nome Completo</FormLabel>
              <Input
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Digite seu nome completo"
              />
              <FormErrorMessage>{errors.name}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.email} isRequired>
              <FormLabel>E-mail</FormLabel>
              <Input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Digite seu e-mail"
              />
              <FormErrorMessage>{errors.email}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.document} isRequired>
              <FormLabel>Documento de Identificação</FormLabel>
              <Input
                name="document"
                value={formData.document}
                onChange={handleChange}
                placeholder="Digite seu documento (CPF, RG, Passaporte, etc.)"
                maxLength={25}
              />
              <FormHelperText>
                Aceita CPF, RG, Passaporte ou outros documentos (3-20 caracteres)
              </FormHelperText>
              <FormErrorMessage>{errors.document}</FormErrorMessage>
            </FormControl>

            <Button
              colorScheme="green"
              type="submit"
              width="full"
              mt={4}
              isLoading={isSubmitting}
              loadingText="Enviando..."
              spinnerPlacement="end"
              spinner={<Spinner size="sm" />}
            >
              Confirmar
            </Button>
          </VStack>
        </form>
      </CardBody>
    </Card>
  );
};

export default RegistrationForm; 